<template>

<div :class="['main', 'list-view', { 'no-sub-header': showDetail }]" ref="top_of_page">

	<div v-if="!showDetail" class="sub-header">
		<div class="input-with-control">
			<input type="search" ref="search_term" v-model="filters.search_term" placeholder="Search..." @search="getList()" v-focus>
			<div class="button action input-go" @click="getList()"><i class="fas fa-search"></i></div>
		</div>
		<div class="controls">
			<pagination-detail v-if="showDetail" :list="list" :index="listIndex" @updatePage="goItem" />
			<pagination v-else :paging="paging" @updatePage="goToPage"></pagination>
		</div>
	</div>

	<div class="filters">

		<div v-if="!showDetail">
			<div class="label-group">
				<label>Start</label>
				<input type="date" v-model="filters.start_date" :max="$today().clone().format('YYYY-MM-DD')">
			</div>
			<div class="label-group">
				<label>End</label>
				<input type="date" v-model="filters.end_date" :max="$today().clone().format('YYYY-MM-DD')">
			</div>
			<div class="label-group">
				<label></label>
				<div class="button small neutral" @click="getList()">Filter Dates</div>
			</div>

			<label>Order Status</label>
			<select v-model="filters.purchase_order_status_id" @change="getList()">
				<option value="">Select...</option>
				<option v-for="(statusCode, index) in statusCodes" :value="statusCode.purchase_order_status_id">{{ statusCode.description }}</option>
			</select>
			<label>Supplier Type</label>
			<select v-model="filters.supplier_type_id" @change="getList()">
				<option value="">Select...</option>
				<option v-for="(supplierType, index) in supplierTypes" :value="supplierType.supplier_type_id">{{ supplierType.description }}</option>
			</select>

			<div class="button neutral" @click="clearFilters(true)">Clear Filters</div>
			<div class="divider"></div>
			<div class="button action" @click="searchNewPO()">New Purchase Order</div>

			<div v-if="legacyLink">
				<div class="divider"></div>
				<router-link :to="legacyLink" class="extra-menu-item">Legacy Version</router-link>
			</div>

		</div>

		<div v-if="showDetail">
			<div :class="['button', canSave ? 'action' : 'disabled']" @click="saveItem()">Save</div>
			<div v-if="detail.purchase_order_status_code != 'DRAFT'" class="note"><i class="fa fa-lock" /> Status must be Draft to save.</div>
			<div :class="['button', canGenerateOrder ? 'neutral' : 'disabled']" @click="showSubmitToSupplier = canGenerateOrder">{{ detail.ordered_date ? 'Res' : 'S'}}ubmit Order</div>
			<div :class="['button', canReceiveItems ? 'neutral' : 'disabled']" @click="receiveItems()">Receive Items</div>
			<div class="divider"></div>
			<div class="button neutral" @click="backToList()">Back to List</div>
			<div class="divider"></div>
			<div class="extra-menu-item" @click="printPurchaseOrder()">Print Purchase Order</div>
			<div class="extra-menu-item" @click="viewAsPDF()">View as PDF</div>
			<div class="extra-menu-item" @click="showCancelPurchaseOrder = true">Cancel Purchase Order</div>
		</div>

	</div>

	<loading v-if="loading" class="loading"></loading>

	<div v-else-if="showDetail" id="detail_purchase_order" class="detail generic-detail">
		<div class="detail-heading" style="grid-template-columns: 1fr 1fr 1fr 0.5fr 0.5fr 0.5fr;">
			<label>Number</label>
			<label>Status</label>
			<label>Total</label>
			<label>Created</label>
			<label>Ordered</label>
			<label>Received</label>
			<div>
				<strong>{{ detail.purchase_order_number }}</strong>
				<div v-if="canSave" class="button icon" @click="doEditPurchaseOrderNumber()"><i class="fa fa-edit" /></div>
			</div>
			<div>
				<strong>{{ statusCodeDescription }}</strong>
				<div class="button icon" @click="doEditStatus()"><i class="fa fa-edit" /></div>
			</div>
			<div>
				<strong>{{ $formatCurrency(purchaseOrderExtendedTotal) }}</strong>
			</div>
			<div>{{ $moment(detail.date_created).format('MM/DD/YYYY') }}</div>
			<div>{{ detail.ordered_date ? $moment(detail.ordered_date).format('MM/DD/YYYY') : 'N/A' }}</div>
			<div>{{ detail.received_date ? $moment(detail.received_date).format('MM/DD/YYYY') : 'N/A' }}</div>
		</div>
		<div class="two-columns">
			<div>
				<h2>Supplier</h2>
				<div class="v-spaced" style="margin: 0 0 1em 0.5em; position: relative;">
					<div>{{ $fullName(supplier) }}</div>
					<div>{{ supplier.address_1 }}</div>
					<div v-if="supplier.address_2">{{ supplier.address_2 }}</div>
					<div>{{ supplier.city }}, {{ supplier.state }} {{ supplier.zip_code }}</div>
					<div class="two-columns v-spaced">
						<div v-if="supplier.ffl_number">
							<label>FFL Number</label> {{ supplier.ffl_number }}
						</div>
						<div v-if="supplier.email_address">
							<label>Email</label> {{ supplier.email_address }}
						</div>
					</div>
					<div v-if="canSave" class="button icon" style="position: absolute; top: -0.5em; right: 0;"><i class="fa fa-edit" @click="doEditContact('supplier')"></i></div>
				</div>
			</div>
			<div>
				<h2>Ship To</h2>
				<div class="v-spaced" style="margin: 0 0 1em 0.5em; position: relative;">
					<div v-if="shipTo.full_name">
						<div>{{ shipTo.full_name }}</div>
						<div>{{ shipTo.address_1 }}</div>
						<div v-if="shipTo.address_2">{{ shipTo.address_2 }}</div>
						<div>{{ shipTo.city }}, {{ shipTo.state }} {{ shipTo.zip_code }}</div>
						<div class="one-two-columns v-spaced">
							<div><label>Phone</label> {{ shipTo.phone_number }}</div>
							<div><label>Email</label> {{ shipTo.email_address }}</div>
						</div>
					</div>
					<div v-if="canSave" class="button icon" style="position: absolute; top: -0.5em; right: 0;"><i class="fa fa-edit" @click="doEditContact('ship_to')"/></div>
				</div>
			</div>
			<div>
				<h2>Terms</h2>
				<div class="two-columns">
					<label>Ship Date</label>
					<label>Cancel By Date</label>
					<input type="date" v-model="detail.ship_date" :disabled="!canSave">
					<input type="date" v-model="detail.cancel_by_date" :disabled="!canSave">
				</div>
				<div class="two-columns">
					<div>
						<label>Discount %</label>
						<input type="text" v-model="detail.discount_amount" :disabled="!canSave">
						<label class="full">Term</label>
						<input type="text" v-model="detail.discount_term" :disabled="!canSave">
						<label class="full">Net Term</label>
						<input type="text" v-model="detail.net_term" :disabled="!canSave">
						<label>Discount Other</label>
						<input type="text" v-model="detail.discount_other" :disabled="!canSave">
					</div>
					<div>
						<label>Shipping Terms</label>
						<input type="text" v-model="detail.shipping_terms" :disabled="!canSave">
						<label>Freight</label>
						<div style="margin: 0.5em 0 1.25em 0.5em;">
							<input type="radio" name="freight" v-model="detail.freight" value="fob" :disabled="!canSave"> FOB
							<input type="radio" name="freight" v-model="detail.freight" value="prepaid" :disabled="!canSave"> Prepaid
						</div>
						<label>Freight Amount</label>
						<input type="text" v-model="detail.shipping_charge" :disabled="!canSave">
					</div>
				</div>
			</div>
			<div>
				<h2>Bill To</h2>
				<div class="v-spaced" style="margin: 0 0 1em 0.5em; position: relative; min-height: 4.5em;">
					<div v-if="billTo.full_name">
						<div>{{ billTo.full_name }}</div>
						<div>{{ billTo.address_1 }}</div>
						<div v-if="billTo.address_2">{{ billTo.address_2 }}</div>
						<div>{{ billTo.city }}, {{ billTo.state }} {{ billTo.zip_code }}</div>
						<div class="one-two-columns v-spaced">
							<div><label>Phone</label> {{ billTo.phone_number }}</div>
							<div><label>Email</label> {{ billTo.email_address }}</div>
						</div>
					</div>
					<div v-if="canSave" class="button icon" style="position: absolute; top: -0.5em; right: 0;"><i class="fa fa-edit" @click="doEditContact('bill_to')"></i></div>
				</div>
				<h2>Notes</h2>
				<div class="v-spaced">
					<div class="two-columns">
						<div>
							<label>Created at Show</label>
							<input type="text" v-model="detail.created_at">
						</div>
						<div>
							<label>Authorized By</label>
							<input type="text" v-model="detail.authorized_by">							
						</div>
					</div>
					<label class="full">Notes</label>
					<textarea v-model="detail.notes" style="height: 4em; margin: 0 0 0.25em 0;"></textarea>
					<div v-if="detail.purchase_order_id" :class="['button', saving ? 'disabled' : 'neutral', 'inline']" @click="saveNotes()">Save Notes</div>
				</div>
			</div>
		</div>
		<h2>Items</h2>
		<table class="data-list">
			<tr>
				<th></th>
				<th>Item</th>
				<th>SKU</th>
				<th class="right">Cost</th>
				<th class="right">Qty</th>
				<th class="right">Ext</th>
				<th v-if="canSave" class="center"></th>
				<th v-else class="right">Rcvd</th>
				<th class="center">Current Inventory</th>
			</tr>
			<tr v-for="(product, index) in products">
				<td class="list center"><div style="width: 70px; height: 36px !important; padding: 0.25em;"><img :src="product.image_src" style="max-height: 100% !important; width: auto;" /></div></td>
				<td class="list">{{ product.description }}<br><label>UPC</label> {{ product.upc }}</td>
				<td class="list">{{ product.sku }}</td>
				<td class="list right">{{ $formatCurrency(product.price) }}</td>
				<td class="list right">{{ product.quantity }}</td>
				<td class="list right">{{ $formatCurrency( Math.round((product.quantity * product.price) * 100) / 100 ) }}</td>
				<td v-if="canSave" class="list center">
					<div class="button icon" @click="editLineItem(product)"><i class="fa fa-edit" /></div>
					<div class="button icon" @click="editLineItemDelete(product)"><i class="fa fa-trash" /></div>
				</td>
				<td v-else class="list right">{{ product.received || 0 }}</td>
				<td class="list">
					<div class="three-columns" style="border-left: 1px dotted #dfdce3; margin: 0 0 0 1em; padding: 0 0 0 1em;">
						<div>
							<label>Cost</label><br>
							{{ product.standard_cost ? $formatCurrency(product.standard_cost) : 'N/A' }}
						</div>
						<div>
							<label>Price</label><br>
							{{ product.pos_price ? $formatCurrency(product.pos_price) : 'N/A' }}
						</div>
						<div>
							<label>On Hand</label><br>
							{{ product.on_hand || 0 }}
						</div>
					</div>
				</td>
			</tr>
			<tr>
				<td></td>
				<td></td>
				<td class="right"><b>Totals</b></td>
				<td class="right"><b>{{ $formatCurrency(purchaseOrderTotal) }}</b></td>
				<td class="right"><b>{{ purchaseOrderTotalQty }}</b></td>
				<td class="right"><b>{{ $formatCurrency(purchaseOrderExtendedTotal) }}</b></td>
				<td></td>
			</tr>
		</table>

		<div v-if="canSave" class="one-two-columns do-not-print" style="margin: 1em 0 0 0;">
			<div>
				<label>Search for a product to add to this purchase order...</label>
				<div class="input-with-control">
					<input type="search" ref="product_search_term" v-model="productSearchTerm" placeholder="UPC or item number..." @search="getListProductSearch()" v-focus>
					<div class="button action input-go" @click="getListProductSearch()"><i class="fas fa-search"></i></div>
				</div>
				<div>
					<div class="button action small" @click="showProductDetail = true"><i class="fa fa-plus"></i></div>
					<label style="display: inline-block; margin: 0 0 0 0.5em;">Or create a new item</label>
				</div>
			</div>
			<div>
				<h3 class="center">{{ searchMessage }}</h3>
				<div v-if="searching" class="center"><i class="fa fa-spinner fa-spin" /></div>
				<ul class="search-list" style="font-size: 0.9em; margin: 1em 0 0 1em;">
					<li v-for="(product, index) in productSearchList" @click="editLineItem(product)" class="clickable-list-item">
						{{ product.upc }} | {{ product.description }}
					</li>
				</ul>
			</div>
		</div>
	</div>

	<div v-else class="list">

	    <div v-if="list.length == 0" class="empty-list">
	        This list is empty
	    </div>
	    <table v-else class="data-list" cellpadding="0" cellspacing="0">
			<thead>
				<tr>
					<th class="list">Number</th>
					<th class="list">Date Created</th>
					<th class="list">Supplier</th>
					<th class="list">Type</th>
					<th class="list">Status</th>
					<th class="list right">Total</th>
				</tr>
			</thead>
			<tr v-for="(purchaseOrder, index) in list" class="clickable-list-item" @click="goItem(purchaseOrder.purchase_order_id)">
				<td class="list">{{ purchaseOrder.purchase_order_number }}</td>
				<td class="list">{{ purchaseOrder.date_created }}</td>
				<td class="list">{{ purchaseOrder.company_name || (purchaseOrder.first_name + ' ' + purchaseOrder.last_name) }}</td>
				<td class="list">{{ purchaseOrder.supplier_type_code.capitalizeWords() }}</td>
				<td class="list">{{ purchaseOrder.status }}</td>
				<td class="list right">{{ $formatCurrency(purchaseOrder.total) }}</td>
			</tr>
		</table>

	</div>

	<div v-if="showNewPO" class="modal medium-modal">
		<div class="inner">
			<h2 v-if="detail.supplier_id">Change Supplier</h2>
			<h2 v-else>New Purchase Order</h2>
			<div class="content">
				<h3 style="text-align: center; margin: 1em 0;">Choose an option to find your supplier...</h3>
				<div class="three-columns" style="grid-template-columns: 1fr 5em 1fr;">
					<div>
						<h2 class="small">Search for a supplier by name</h2>
						<div class="input-with-control" style="margin: 1em 0;">
							<input type="search" v-model="supplierSearchTerm" placeholder="Search..." @search="getListSuppliers()" ref="supplier_search_term">
							<div :class="['button', supplierSearchTerm ? 'action' : 'disabled', 'input-go']" @click="getListSuppliers()"><i class="fas fa-search"></i></div>
							<div :class="['search-message-under', { show: searchMessage }]">{{ searchMessage }}</div>
						</div>
						<label>Search for a supplier already in the system or one you've received from before.</label>
					</div>
					<div class="divider"><span class="divider-label"><i>OR</i></span></div>
					<div>
						<h2 class="small">Search for a supplier by FFL Number</h2>
						<div style="margin: 1em 0;">
							<ffl-number-search @searchResult="setList" />
						</div>
						<label>Enter an FFL Number to search the complete national database of over 80,000 FFLs.</label>
					</div>
				</div>
				<div class="divider" style="margin: 2em 4em;"><span class="divider-label"><i>OR</i></span></div>
				<div class="center" style="margin: 0 0 2em 0;">
					<label>Can't find your supplier?</label>
					<div class="button neutral small" style="margin: 0 0 0 1em;" @click="createSupplier()">Create a New Supplier</div>
				</div>

				<div v-if="supplierSearchList.length > 0" class="message-overlay">
					<label>{{ supplierSearchList.length > 1 ? 'Multiple suppliers' : 'One supplier' }} found...</label>
					<ul class="search-list" style="font-size: 0.9em; margin: 1em 0 0 0;">
						<li class="clickable-list-item" v-for="(supplier, index) in supplierSearchList" @click="assignSupplier(supplier)">
							<div class="two-columns left">
								<div>
									{{ supplier.full_name }}<br>
									<label v-if="supplier.ffl_number">FFL</label> {{ supplier.ffl_number }}
									<label v-if="supplier.drivers_license_number">DL</label> {{ supplier.drivers_license_number }}
								</div>
								<div>
									{{ supplier.address_1 }}<br>{{ supplier.city }} {{ supplier.state }} {{ supplier.zip_code }}<br>
								</div>
							</div>
						</li>
					</ul>
				</div>

				<div v-if="enteringPONumber" class="message-overlay">
					<div style="font-size: 0.9em; margin: 2em auto; width: 40em; text-align: left;" class="two-columns">
						<div>
							{{ supplier.full_name }}<br>
							{{ supplier.address_1 }}<br>{{ supplier.city }} {{ supplier.state }} {{ supplier.zip_code }}<br><br>
							<label v-if="supplier.ffl_number">FFL</label> {{ supplier.ffl_number }}<br>
							<label v-if="supplier.drivers_license_number">DL</label> {{ supplier.drivers_license_number }}
						</div>
						<div>
							<label>Enter a {{ detail.purchase_order_number ? '' : 'new' }} PO Number</label>
							<input type="text" v-model="detail.purchase_order_number" @blur="validatePONumber" >
						</div>
					</div>
					<p v-if="saving"><i class="fa fa-spinner fa-spin"></i></p>
					<p v-if="savingMessage">{{ savingMessage }}</p>
				</div>

			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showNewPO = false">Cancel</div>
				<div v-if="supplierSearchList.length > 0" @click="supplierSearchList = []" class="button neutral spaced">Search Again</div>
				<div v-if="enteringPONumber" :class="['button', detail.purchase_order_number ? 'action' : 'disabled']" @click="saveItem()">{{ detail.purchase_order_id ? 'Update' : 'Create' }} Purchase Order</div>
			</div>
		</div>
	</div>

	<div v-if="showEditContact" :class="['modal', { 'large-modal': showEditContact.indexOf('supplier') > -1 }]">
		<div class="inner" style="min-width: 70vw; max-height: 80vh;">
			<h2>Edit {{ showEditContact.capitalizeWords() }}</h2>
			<div class="content" style="max-height: 60vh;">
				<div v-if="showEditContact.indexOf('supplier') > -1" class="three-columns">
					<div>
						<label-required label="Supplier Type" :condition="editContact.supplier_type_id" />
						<select v-model="editContact.supplier_type_id" :disabled="editContact.client_id != clientId">
							<option value=''>Select...</option>
							<option v-for="(supplierType, index) in supplierTypes" :value="supplierType.supplier_type_id">{{ supplierType.description }}</option>
						</select>
					</div>
				</div>
				<contact-detail-form :clientId="clientId" :contact="editContact" :hideControls="true" :addressOnly="showEditContact.indexOf('supplier') < 0" @update="updateEditContact" />
			</div>
			<div class="controls">
				<div class="button spaced cancel" @click="showEditContact = ''">Cancel</div>
				<div v-if="showEditContact == 'new_supplier'" :class="['button', 'spaced', canSaveContact && !saving ? 'action' : 'disabled']" @click="saveSupplier()">Save New Supplier</div>
				<div v-else-if="editContact.client_id == clientId" :class="['button', 'spaced', canSaveContact && !saving ? 'action' : 'disabled']" @click="saveEditContact()">Save {{ showEditContact.capitalizeWords() }}</div>
				<div v-if="showEditContact == 'supplier'" class="button neutral spaced" @click="searchNewPO()">Change Supplier</div>
				<div v-if="editContactMessage" class="message">{{ editContactMessage }}</div>
			</div>
		</div>
	</div>

	<div v-if="editingPurchaseOrderNumber" class="modal">
		<div class="inner">
			<h2>Purchase Order Number</h2>
			<div class="content">
				<input type="text" v-model="editDetail.purchase_order_number" />
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="editingPurchaseOrderNumber = false">Cancel</div>
				<div :class="['button', editDetail.purchase_order_number ? 'action' : 'disabled', 'spaced']" @click="savePurchaseOrderNumber()">Save Number</div>
			</div>
		</div>
	</div>

	<div v-if="showEditStatus" class="modal">
		<div class="inner">
			<h2>Purchase Order Status</h2>
			<div class="content">
				<p>You can manually change the status of this purchase order.</p>
				<select v-model="editStatus">
					<option v-for="(statusCode, index) in statusCodes" :value="statusCode.purchase_order_status_id">{{ statusCode.description }}</option>
				</select>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showEditStatus = false">Cancel</div>
				<div class="button action spaced" @click="saveStatus()">Save Status</div>
				<div v-if="savingMessage" class="note">{{ savingMessage }}</div>
			</div>
		</div>
	</div>

	<div v-if="showCancelPurchaseOrder" class="modal">
		<div class="inner">
			<h2>Cancel Purchase Order</h2>
			<div class="content">
				<p>This Purchase Order's status is currently <b>{{ statusCodeDescription }}</b>.</p>
				<p v-if="detail.purchase_order_status_code != 'DRAFT'">You'll need to contact {{ supplier.company_name || supplier.first_name + ' ' + supplier.last_name }} directly if you need them to modify or cancel this order.</p>
				<p>Are you sure you want to set this order's status to Canceled?</p>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showCancelPurchaseOrder = false">No</div>
				<div class="button action spaced" @click="cancelPurchaseOrder()">Yes, Cancel</div>
				<div v-if="savingMessage" class="note">{{ savingMessage }}</div>
			</div>
		</div>
	</div>

	<div v-if="showSubmitToSupplier" class="modal">
		<div class="inner">
			<h2>{{ detail.ordered_date ? 'Res' : 'S'}}ubmit Purchase Order</h2>
			<div class="content">
				<p><span v-if="detail.ordered_date">This Purchase Order was submitted on {{ $moment(detail.ordered_date).format('MM/DD/YYYY') }}. You can resubmit if necessary.</span> We recommend that you preview the PDF before {{ detail.ordered_date ? 're' : '' }}submitting, and double check that the Email To address is correct.</p>
				<label class="full">Email To</label>
				<input type="text" v-model="submitToSupplierEmail" />
				<label class="full">Subject</label>
				<input type="text" v-model="submitToSupplierSubject" />
				<label class="full">Message</label>
				<textarea v-model="submitToSupplierMessage" style="height: 5em;"></textarea>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showSubmitToSupplier = false">Cancel</div>
				<div class="button neutral spaced" @click="viewAsPDF()">Preview</div>
				<div :class="['button', !saving && submitToSupplierEmail && submitToSupplierSubject && submitToSupplierMessage ? 'action' : 'disabled', 'spaced']" @click="submitToSupplier()">{{ detail.ordered_date ? 'Res' : 'S' }}ubmit To Supplier</div>
				<div v-if="savingMessage" class="note v-spaced">{{ savingMessage }}</div>
			</div>
		</div>
	</div>

	<div v-if="showProductDetail" class="modal large-modal">
		<div class="inner">
			<h2>Purchase Order Item</h2>
			<div class="content">
				<product-detail-form :product="productDetail" :hideControls="true" :createProduct="true" :noCancel="true" @save="updateProduct" :key="'product_detail_component_' + productDetail.new_product + productDetail.new_image"></product-detail-form>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showProductDetail = false">Cancel</div>
				<div :class="['button', canSaveNewProduct ? 'action' : 'disabled', 'spaced']" @click="saveNewProduct()">Add This Item</div>
				<div v-if="editProductMessage" class="message">{{ editProductMessage }}</div>
			</div>
		</div>
	</div>

	<div v-if="showEditProduct" class="modal">
		<div class="inner">
			<h2 v-if="editProduct.delete">Remove Line Item</h2>
			<h2 v-else>{{ editProduct.purchase_order_product_id ? 'Edit' : 'Add' }} Line Item</h2>
			<div class="content">
				<div class="two-columns" style="margin: 0 0 1em 0;">
					<div><img :src="editProduct.image_src" style="max-width: 140px;" /></div>
					<div>
						<div>{{ editProduct.description }}</div>
						<div><label>UPC</label> {{ editProduct.upc }}</div>
						<div v-if="editProduct.inventory" class="v-spaced">
							<label>Current Inventory</label>
							<div style="font-size: 0.9em; margin: 0 1em 0 0.5em;">
							<div class="two-columns"><label>Cost</label><div class="right">{{ $formatCurrency(editProduct.inventory.cost) }}</div></div>
							<div class="two-columns"><label>Price</label><div class="right">{{ $formatCurrency(editProduct.inventory.pos) }}</div></div>
							<div class="two-columns"><label>On Hand</label><div class="right">{{ editProduct.inventory.store_available }}</div></div>
						</div>
					</div>
					</div>
				</div>
				<div class="three-columns">
					<div>
						<label-required label="Supplier SKU" :condition="editProduct.sku" />
						<input type="text" v-model="editProduct.sku" :disabled="editProduct.delete" />
					</div>
					<div>
						<label-required label="Purchase Order Cost" :condition="editProduct.price" />
						<number-input :decimals='2' v-model="editProduct.price" :disabled="editProduct.delete"/>
					</div>
					<div>
						<label-required label="Quantity Ordered" :condition="editProduct.quantity" />
						<number-input :decimals='0' v-model="editProduct.quantity" :disabled="editProduct.delete"/>
					</div>
				</div>
				<div v-if="editProduct.delete" class="center" style="padding: 0.25em;">
					Are you sure you want to remove this line item?
				</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showEditProduct = false">Cancel</div>
				<div v-if="editProduct.delete" :class="['button', saving ? 'disabled' : 'action', 'spaced']" @click="deleteLineItem()">Yes, Remove Line Item</div>
				<div v-else :class="['button', saving ? 'disabled' : 'action', 'spaced']" @click="saveLineItem()">Save Line Item</div>
				<div v-if="saveLineItemMessage" class="v-spaced">{{ saveLineItemMessage }}</div>
			</div>
		</div>
	</div>

	<message-modal v-if="message" :message="message" :loading="saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<iframe id="download" src="" style="display: none;"></iframe>

	<help-popup :helpLink="{248:'Purchase Orders', 249:'Creating a Purchase Order', 250:'Receiving From a Purchase Order'}" :tours="false"></help-popup>

</div>

</template>

<script>
import Loading from '@/components/Loading.vue'
import FFLNumberSearch from "@/components/FFLNumberSearch.vue"
import ProductDetailMinimum from '@/components/ProductDetailMinimum.vue'
import ContactDetailForm from '@/components/ContactDetailForm'
import LabelRequired from '@/components/LabelRequired'
import NumberInput from '@/components/NumberInput.vue'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'purchase-orders',
    props: ['purchase_order_id'],
    components: {
        'loading': Loading,
		'ffl-number-search': FFLNumberSearch,
		'product-detail-form': ProductDetailMinimum,
        'contact-detail-form': ContactDetailForm,
		'label-required': LabelRequired,
		'number-input': NumberInput,
		'help-popup': HelpPopup
    },
	data() {
		return {
            loading: false,
			clientId: '',
			filters: {},
			filterMessage: '',
            statusCodes: [],
            supplierTypes: [],
			list: [],
			listIndex: 0,
			paging: {},
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			showDetail: false,
			detail: {},
			supplier: {},
			shipTo: {},
			billTo: {},
			products: [],
			showNewPO: false,
			supplierSearchTerm: '',
			supplierSearchList: [],
			searching: false,
			searchMessage: '',
			enteringPONumber: false,
			creatingPO: false,
			saving: false,
			savingMessage: '',
			productDetail: {},
			showProductDetail: false,
			editProductMessage: '',

			editingPurchaseOrderNumber: false,

			showEditContact: '',
			editContact: {},
			editContactMessage: '',

			productSearchTerm: '',
			productSearchList: [],
			editProduct: {},
			showEditProduct: false,
			saveLineItemMessage: '',

			showEditStatus: false,
			editStatus: '',
			showCancelPurchaseOrder: false,

			showSubmitToSupplier: false,
			submitToSupplierSubject: '',
			submitToSupplierMessage: '',
			submitToSupplierEmail: '',

			legacyLink: ''

		}
	},
	created() {
		if (this.purchase_order_id) {
			if (!this.filters.page) {
				this.clearFilters()
			}
			this.getItem(this.purchase_order_id)
		} else {
			this.clearFilters()
			this.getList()
		}
	},
	computed: {
		statusCodeDescription() {
			return this.statusCodes.filter(code => code.purchase_order_status_id == this.detail.purchase_order_status_id)[0].description
		},
		canSave() {
			return !this.saving && this.detail.purchase_order_status_id && this.detail.purchase_order_status_code == 'DRAFT'
		},
		canGenerateOrder() {
			return this.detail.purchase_order_status_id && ['DRAFT', 'OPEN'].includes(this.detail.purchase_order_status_code) && this.products.length > 0
		},
		canReceiveItems() {
			return this.detail.purchase_order_status_id && ['OPEN', 'RECEIVED_PARTIAL'].includes(this.detail.purchase_order_status_code) && this.products.length > 0
		},
		canSaveContact() {
			return (this.editContact.full_name || this.editContact.company_name || this.editContact.last_name) && this.editContact.address_1 && this.editContact.city && this.editContact.state && this.editContact.zip_code
		},
		canSaveNewProduct() {
			return this.editProduct.upc && this.editProduct.description && this.editProduct.category_id && this.editProduct.product_type_id
		},
		purchaseOrderTotal() {
			return this.products.map(product => product.price).reduce((a, b) => a * 1 + b * 1, 0)
		},
		purchaseOrderTotalQty() {
			return this.products.map(product => product.quantity).reduce((a, b) => a * 1 + b * 1, 0)
		},
		purchaseOrderExtendedTotal() {
			let total = 0
			this.products.forEach(product => { total += (product.price * 1) * (product.quantity * 1) })
			return total
		}
	},
	watch: {
		purchase_order_id: function() {
			if (this.purchase_order_id) {
				this.getItem(this.purchase_order_id)
			} else {
				this.getList()
			}
		}
	},
	methods: {
		clearFilters(getList) {
			this.filters = {
				page: 1,
				search_term: '',
                purchase_order_status_id: '',
                supplier_type_id: '',
                start_date: '',
                end_date: '',
				internal_use_only: 0,
				inactive: 0,
				do_export: 0
			}
			if (getList) {
				this.getList()
			}
		},
		getListStatusCodes() {
			this.apiCall({ destination: 'get_list_purchase_order_status_codes', data: this.filters }).then(obj => {
				this.clientId = obj.client_id
				this.statusCodes = obj.status_codes
				this.supplierTypes = obj.supplier_types
			}).catch(err => {
				console.log(err)
			})
		},
		getList() {
			if (this.loading) { return }
			this.loading = true
			this.message = 'Loading purchase orders...'
			this.messageStatus = 'waiting'
            if (this.statusCodes.length == 0) {
				this.getListStatusCodes();
            }
			this.detail = {}
			this.supplier = {}
			this.showDetail = false
			this.apiCall({ destination: 'get_list_purchase_orders', data: this.filters }).then(obj => {
				this.loading = false
				this.messageStatus = obj.status
				this.clientId = obj.client_id
				if (this.filters.do_export) {
					this.filters.do_export = 0
					var outputFile = obj.file_name
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = "//" + document.location.hostname + "/download.php?file_name=" + obj.file_name + "&output_file_name=" + outputFile
						this.gettingExport = false
						this.message = ''
					}, 1500)
				} else {
                    if (obj.list) {
    					this.list = obj.list
                    }
					if (obj.paging) {
						this.paging = obj.paging
					}
					if (obj.legacy_link) {
						this.legacyLink = obj.legacy_link
					}
					this.message = ''
				}
			}).catch(err => {
				console.log(err)
			})
		},
		searchNewPO() {
			this.searching = false
			this.creatingPO = false
			// this.supplier = {}
			this.searchMessage = ''
			this.supplierSearchList = []
			this.enteringPONumber = false
			this.showEditContact = false
			this.showNewPO = true
		},
		getListSuppliers(context) {
			if (this.searching) { return }
			this.searching = true
			this.searchMessage = ''
			this.supplierSearchList = []
			this.apiCall({ destination: 'get_list_inventory_suppliers', data: { search_term: this.supplierSearchTerm } }).then(obj => {
				this.searching = false
				if (obj.suppliers.length > 0) {
					this.supplierSearchList = obj.suppliers
				} else {
					this.searchMessage = 'Supplier not found'
					setTimeout(() => { this.searchMessage = '' }, 2000)
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setList(list) {
			this.supplierSearchList = list
		},
		assignSupplier(supplier) {
			if (supplier.ffl_list_id) {
				this.apiCall({ destination: 'get_supplier_from_ffl_search', data: supplier }).then(obj => {
					if (obj.status == 'success') {
						this.createPO(obj.supplier_detail)
					} else {
						this.message = obj.message
					}
				}).catch(err => {
					console.log(err)
				})
			} else {
				this.createPO(supplier)
			}
		},
		createPO(supplier) {
			this.detail.supplier_id = supplier.supplier_id
			this.supplier = supplier
			this.statusCodes.forEach(statusCode => { 
				if (statusCode.purchase_order_status_code == 'DRAFT') {
					this.detail.purchase_order_status_id = statusCode.purchase_order_status_id
					this.detail.purchase_order_status_code = 'DRAFT'
				}
			})
			this.searchMessage = ''
			this.supplierSearchList = []
			this.enteringPONumber = true
		},
		createSupplier() {
			this.doEditContact('new_supplier')
		},
		saveSupplier() {
			if (!this.canSaveContact) { return }
			this.saving = true
			this.editContactMessage = 'Creating new supplier...'
			this.apiCall({ destination: 'save_item_supplier', data: this.editContact }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.showEditContact = ''
					this.editContactMessage = ''
					this.editContact.supplier_id = obj.supplier_id
					this.editContact.full_name = this.editContact.company_name || this.editContact.first_name + ' ' + this.editContact.last_name
					this.createPO(this.editContact)
				} else {
					this.editContactMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		goItem(item) {
			let purchaseOrderId = (typeof item == 'object') ? item.purchase_order_id : item
			if (purchaseOrderId == this.purchase_order_id) { return }
			// set the listIndex for prev / next controls in detail
			for (let i = 0; i < this.list.length; i++) {
				if (this.list[i].purchase_order_id == purchaseOrderId) {
					this.listIndex = i
					break
				}
			}
			this.$router.push('/orders-sales/purchase-orders/' + purchaseOrderId)
		},
		getItem(purchaseOrderId) {
            if (this.statusCodes.length == 0) {
				this.getListStatusCodes();
            }
			if (this.loading) { return }
			this.loading = true
			this.apiCall({ destination: 'get_item_purchase_order', data: { purchase_order_id: purchaseOrderId } }).then(obj => {
				this.loading = false
				this.clientId = obj.client_id
				if (obj.status == 'success') {
					this.detail = obj.detail
					if (obj.supplier) {
						this.supplier = obj.supplier
					}
					if (obj.ship_to) {
						this.shipTo = obj.ship_to
					}
					if (obj.bill_to) {
						this.billTo = obj.bill_to
					}
					this.getListPurchaseOrderProducts(purchaseOrderId)
					this.submitToSupplierEmail = this.supplier.email_address
					this.submitToSupplierSubject = 'New Purchase Order for ' + this.getClient.company_name
					this.submitToSupplierMessage = 'Attached is a new Purchase Order for ' + this.getClient.company_name + '.'
					this.showDetail = true
					// slight delay while dom renders detail div
					setTimeout(() => { document.getElementById('detail_purchase_order').scrollTop = 0 }, 100)					
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getListPurchaseOrderProducts(purchaseOrderId) {
			let postData = {
				purchase_order_id: purchaseOrderId, 
				supplier_id: this.supplier.supplier_id,
				distributor_id: this.supplier.distributor_id,
				purchase_order_status_code: this.detail.purchase_order_status_code
			}
			this.apiCall({ destination: 'get_list_purchase_order_products', data: postData }).then(obj => {
				if (obj.products) {
					this.products = obj.products
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getListProductSearch() {
			if (!this.productSearchTerm) {
				this.searchMessage = ''
				this.searching = false
				return
			}
			if (this.searching) { return }
			this.searchMessage = 'Searching products...'
			this.searching = true
			this.apiCall({ destination: 'get_list_purchase_order_search_products', data: { search_term: this.productSearchTerm, distributor_id: this.supplier.distributor_id } }).then(obj => {
				this.searching = false
				if (obj.status == 'success') {
					// if zero results, update message
					// if exactly one result, assign to purchase order
					// if mulitple resultes, display list
					if (obj.list.length == 0) {
						if (this.supplier.distributor_id) {
							this.searchMessage = 'No ' + this.supplier.distributor_code + ' products found with that UPC or item number.'
						} else {
							this.searchMessage = 'No products found with that UPC or item number.'
						}
					} else if (obj.list.length == 1) {
						this.searchMessage = ''
						this.editLineItem(obj.list[0])
					} else {
						this.productSearchList = obj.list
						this.searchMessage = 'Multiple products found...'
					}
				} else {
					this.searchMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		doEditContact(which) {
			switch (which) {
				case 'ship_to':
					this.editContact = this.$copy(this.shipTo)
					break;
				case 'bill_to':
					this.editContact = this.$copy(this.billTo)
					break;
				case 'supplier':
					this.editContact = this.$copy(this.supplier)
					break;
				case 'new_supplier':
					this.editContact = { 
						client_id: this.clientId,
						notes: [{ note: '', priority: '' }]
					}
					break;
			}
			this.showEditContact = which
		},
		updateEditContact(contact) {
			this.editContactMessage = ''
			this.editContact = contact
		},
		saveEditContact() {
			if (this.saving) { return }
			this.saving = true
			this.editContactMessage = 'Saving ' + this.showEditContact.capitalizeWords() + '...'
			this.editContact.context = this.showEditContact
			this.editContact.purchase_order_id = this.purchase_order_id
			this.apiCall({ destination: 'save_item_purchase_order_contact', data: this.editContact }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.getItem(this.purchase_order_id)
					this.showEditContact = ''
					this.editContactMessage = ''
					this.editContact = {}
				} else {
					this.editContactMessage = obj.message
				}
				// slight delay while dom renders detail div
				setTimeout(() => { document.getElementById('detail_purchase_order').scrollTop = 0 }, 100)					
			}).catch(err => {
				console.log(err)
			})
		},
		editLineItem(product) {
			this.editProduct = this.$copy(product)
			this.saveLineItemMessage = ''
			this.showEditProduct = true
			this.productSearchTerm = ''
			this.productSearchList = []
			this.searchMessage = ''
		},
		editLineItemDelete(product) {
			product.delete = 1
			this.editProduct = this.$copy(product)
			this.saveLineItemMessage = ''
			this.showEditProduct = true
		},
		saveLineItem() {
			if (this.saving) { return }
			if (!this.editProduct.delete && (!this.editProduct.sku || !this.editProduct.price || !this.editProduct.quantity)) { 
				this.saveLineItemMessage = 'SKU, Cost and Qty are required.'
				return 
			}
			this.saving = true
			this.saveLineItemMessage = ''
			let postData = {
				purchase_order_id: this.detail.purchase_order_id,
				supplier_id: this.supplier.supplier_id,
				distributor_id: this.supplier.distributor_id,
				product_id: this.editProduct.product_id,
				sku: this.editProduct.sku,
				price: this.editProduct.price,
				quantity: this.editProduct.quantity
			}
			this.apiCall({ destination: 'save_item_purchase_order_product', data: postData }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.getListPurchaseOrderProducts(this.detail.purchase_order_id)
					this.showEditProduct = false
					this.editProduct = {}
				} else {
					this.saveLineItemMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		deleteLineItem() {
			if (this.saving) { return }
			this.saving = true
			let postData = {
				purchase_order_id: this.detail.purchase_order_id,
				product_id: this.editProduct.product_id,
				delete: true
			}
			this.apiCall({ destination: 'save_item_purchase_order_product', data: postData }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.getListPurchaseOrderProducts(this.detail.purchase_order_id)
					this.showEditProduct = false
					this.editProduct = {}
				} else {
					this.saveLineItemMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		updateProduct(productDetail) {
			// called by <product-detail-form>
			this.editProduct = productDetail
		},
		saveNewProduct() {
			if (this.saving) { return }
			if (!this.canSaveNewProduct) { return }
			this.editProductMessage = 'Saving new product...'
			this.saving = true
			this.editProduct.client_id = this.clientId
			this.apiCall({ destination: 'save_item_product_info', data: this.editProduct }).then(obj => {
				this.saving = false
				this.editProductMessage = obj.message
				if (obj.status == 'success') {
					this.editProduct.product_id = obj.product_id
					this.showProductDetail = false
					this.showEditProduct = true
				}
			}).catch(err => {
				console.log(err)
			})
		},
		doEditPurchaseOrderNumber() {
			this.editDetail = this.$copy(this.detail)
			this.editingPurchaseOrderNumber = true
		},
		savePurchaseOrderNumber() {
			if (this.saving || !this.editDetail.purchase_order_number) { return }
			this.saving = true
			this.message = ''
			this.messageStatus = ''
			this.editDetail.purchase_order_number = this.$formatFileName(this.editDetail.purchase_order_number, 100)

			this.apiCall({ destination: 'save_item_purchase_order_number', data: { purchase_order_id: this.detail.purchase_order_id, purchase_order_number: this.editDetail.purchase_order_number } }).then(obj => {
				this.saving = false
				this.editingPurchaseOrderNumber = false
				// slight delay while dom renders detail div
				setTimeout(() => { document.getElementById('detail_purchase_order').scrollTop = 0 }, 100)					
				if (obj.status == 'success') {
					this.detail.purchase_order_number = this.editDetail.purchase_order_number
				} else {
					this.message = obj.message
					this.messageStatus = 'error'
				}
			}).catch(err => {
				console.log(err)
			})
		},
		doEditStatus() {
			this.editStatus = this.$copy(this.detail.purchase_order_status_id)
			this.showEditStatus = true
		},
		saveStatus() {
			if (this.saving || !this.editStatus) { return }
			this.saving = true
			this.savingMessage = 'Updating status...'
			this.apiCall({ destination: 'save_item_purchase_order_status', data: { purchase_order_id: this.detail.purchase_order_id, purchase_order_status_id: this.editStatus } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.detail.purchase_order_status_id = this.editStatus
					this.detail.purchase_order_status_code = obj.purchase_order_status_code
					this.showEditStatus = false
					this.editStatus = ''
					// slight delay while dom renders detail div
					setTimeout(() => { document.getElementById('detail_purchase_order').scrollTop = 0 }, 100)					
				} else {
					this.savingMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		cancelPurchaseOrder() {
			if (this.saving) { return }
			this.saving = true
			this.savingMessage = 'Setting status to Canceled...'
			this.apiCall({ destination: 'save_item_purchase_order_status', data: { purchase_order_id: this.detail.purchase_order_id, purchase_order_status_code: 'CANCELED' } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.showCancelPurchaseOrder = false
					this.savingMessage = ''
					this.message = 'Status has been set to Canceled!'
					this.messageTimeout = 2
					setTimeout(() => { this.getList() }, 2000)					
				} else {
					this.savingMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveItem() {
			if (this.saving || !this.canSave) { return }
			this.saving = true
			this.message = 'Saving purchase order...'
			this.messageTimeout = 0
			this.apiCall({ destination: 'save_item_purchase_order', data: this.detail }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.enteringPONumber = false
					this.creatingPO = false
					this.showNewPO = false
					this.detail.purchase_order_id = obj.purchase_order_id
					this.message = 'Purchase order saved!'
					this.messageStatus = 'success'
					this.messageTimeout = 2
					if (this.showDetail) {
						this.getItem(this.purchase_order_id)
					} else {
						this.$router.push('/orders-sales/purchase-orders/' + this.detail.purchase_order_id)
					}
				} else {
					this.messageStatus = 'error'
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveNotes() {
			if (this.saving || !this.canSave) { return }
			this.saving = true
			this.message = 'Saving purchase order notes...'
			this.messageTimeout = 0
			this.apiCall({ destination: 'save_item_purchase_order_notes', data: this.detail }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'Purchase order notes saved!'
					this.messageTimeout = 2
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		backToList() {
			this.$router.push('/orders-sales/purchase-orders/')
		},
		doExport() {
			if (this.gettingExport) {
				return false
			}
			this.gettingExport = true
			this.exportMessage = 'Generating CSV for ' + this.list.length + ' items...'
			this.filters.product_id = ''
			this.filters.do_export = true
			this.getList()
		},
		receiveItems() {
			if (!this.canReceiveItems) { return }
			this.$router.push('/inventory/receive-inventory?purchase_order_number=' + this.detail.purchase_order_number)
		},
		printPurchaseOrder() {
			window.print()
		},
		viewAsPDF() {
			if (!this.purchase_order_id) { return }
			let url = this.getPDFAPIEndpoint + '?action=purchase_order_pdf&source=orders_sales&fd_token=' + getCookie('dashboard_token') + '&id=' + this.$randomString(6) + this.purchase_order_id + this.$randomString(6) + this.id + this.$randomString(6) + this.clientId + this.$randomString(3)
			window.open(url, '_blank')
		},
		submitToSupplier() {
			this.savingMessage = ""
			if (this.saving) { return }
			if (!this.submitToSupplierEmail || !this.submitToSupplierSubject || !this.submitToSupplierMessage) { return }
			if (!this.$isValidEmailAddress(this.submitToSupplierEmail)) {
				this.savingMessage = "Please enter a valid Email To address"
				return
			}
			this.showSubmitToSupplier = false
			this.saving = true
			this.message = 'Emailing purchase order to supplier...'
			this.messageTimeout = 0
			let postData = {
				client_id: this.client_id, 
				purchase_order_id: this.purchase_order_id, 
				submit_to_supplier: 1,
				supplier_email: this.submitToSupplierEmail,
				subject: this.submitToSupplierSubject,
				message: this.submitToSupplierMessage
			}
			this.apiCall({ destination: this.getPDFAPIEndpoint + '?action=purchase_order_pdf', data: postData }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.message = 'Purchase order sent!'
					this.messageStatus = 'success'
					this.messageTimeout = 2
					this.getItem(this.purchase_order_id)
				} else {
					this.messageStatus = 'error'
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		validatePONumber() {
			this.detail.purchase_order_number = this.$formatFileName(this.detail.purchase_order_number, 100);
		}
	}
}
</script>

<style lang="scss" scoped>
</style>
